/**
 * constellationCatalog — the editor's bundled, hand-verified star catalog.
 *
 * Keyed by constellation name → { starName: { ra, dec, connectsTo } }, where
 * `ra`/`dec` are decimal degrees (J2000) and `connectsTo` lists every figure
 * neighbour (bidirectional). Stars are keyed by proper name where one exists,
 * otherwise a Bayer designation. This is the single source of truth for the Stars
 * menu and the generated per-constellation files.
 *
 * The Virgo/Leo/Ursa Minor/Bootes/Draco figures are converted from the sibling
 * astrotours repo (`src/core/constellationData.js`, id-keyed with directed lines)
 * into this name-keyed, bidirectional form. The rest are hand-verified additions.
 *
 * Invariants (see constellationCatalog.test.js): unique slugs, in-range coordinates,
 * every connectsTo resolves within the same constellation, symmetry, no orphans.
 */

export const CATALOG = {
  'Ursa Major': {
    // Big Dipper (7 stars). Coordinates: Wikipedia star articles (J2000).
    Dubhe: { ra: 165.932, dec: 61.751, connectsTo: ['Merak', 'Megrez'] },
    Merak: { ra: 165.46, dec: 56.383, connectsTo: ['Dubhe', 'Phecda'] },
    Phecda: { ra: 178.458, dec: 53.695, connectsTo: ['Merak', 'Megrez'] },
    Megrez: {
      ra: 183.857,
      dec: 57.033,
      connectsTo: ['Dubhe', 'Phecda', 'Alioth']
    },
    Alioth: { ra: 193.507, dec: 55.96, connectsTo: ['Megrez', 'Mizar'] },
    Mizar: { ra: 200.981, dec: 54.925, connectsTo: ['Alioth', 'Alkaid'] },
    Alkaid: { ra: 206.885, dec: 49.313, connectsTo: ['Mizar'] }
  },

  Virgo: {
    // Converted from astrotours constellationData.js.
    Spica: { ra: 201.298, dec: -11.161, connectsTo: ['Porrima', 'Heze'] },
    Porrima: {
      ra: 190.415,
      dec: -1.449,
      connectsTo: ['Zaniah', 'Auva', 'Spica']
    },
    Auva: { ra: 193.901, dec: 3.397, connectsTo: ['Porrima', 'Vindemiatrix'] },
    Vindemiatrix: { ra: 195.544, dec: 10.959, connectsTo: ['Auva'] },
    Heze: { ra: 203.673, dec: -0.596, connectsTo: ['Spica', 'Tau Virginis'] },
    Zaniah: {
      ra: 184.976,
      dec: -0.667,
      connectsTo: ['Nu Virginis', 'Porrima']
    },
    Syrma: {
      ra: 214.004,
      dec: -6.001,
      connectsTo: ['Tau Virginis', 'Rijl al Awwa']
    },
    'Rijl al Awwa': { ra: 220.765, dec: -5.658, connectsTo: ['Syrma'] },
    'Nu Virginis': { ra: 176.465, dec: 6.529, connectsTo: ['Zaniah'] },
    'Tau Virginis': { ra: 210.412, dec: 1.544, connectsTo: ['Heze', 'Syrma'] }
  },

  Leo: {
    // Converted from astrotours constellationData.js.
    Regulus: { ra: 152.093, dec: 11.967, connectsTo: ['Al Jabhah', 'Chertan'] },
    Denebola: { ra: 177.265, dec: 14.572, connectsTo: ['Chertan', 'Zosma'] },
    Algieba: {
      ra: 154.993,
      dec: 19.842,
      connectsTo: ['Adhafera', 'Al Jabhah', 'Zosma']
    },
    Zosma: {
      ra: 168.527,
      dec: 20.524,
      connectsTo: ['Chertan', 'Denebola', 'Algieba']
    },
    Algenubi: { ra: 146.463, dec: 23.774, connectsTo: ['Rasalas', 'Adhafera'] },
    Adhafera: {
      ra: 154.172,
      dec: 23.417,
      connectsTo: ['Algenubi', 'Algieba', 'Rasalas']
    },
    'Al Jabhah': {
      ra: 151.833,
      dec: 16.763,
      connectsTo: ['Algieba', 'Regulus']
    },
    Chertan: {
      ra: 168.56,
      dec: 15.43,
      connectsTo: ['Regulus', 'Denebola', 'Zosma']
    },
    Rasalas: { ra: 148.191, dec: 26.007, connectsTo: ['Algenubi', 'Adhafera'] }
  },

  'Ursa Minor': {
    // Converted from astrotours constellationData.js.
    Polaris: { ra: 37.954, dec: 89.264, connectsTo: ['Yildun'] },
    Kochab: {
      ra: 222.676,
      dec: 74.156,
      connectsTo: ['Ahfa al Farkadain', 'Pherkad']
    },
    Pherkad: {
      ra: 230.182,
      dec: 71.834,
      connectsTo: ['Kochab', 'Anwar al Farkadain']
    },
    Yildun: { ra: 263.054, dec: 86.586, connectsTo: ['Polaris', 'Urodelus'] },
    Urodelus: {
      ra: 251.493,
      dec: 82.037,
      connectsTo: ['Yildun', 'Ahfa al Farkadain']
    },
    'Ahfa al Farkadain': {
      ra: 236.015,
      dec: 77.794,
      connectsTo: ['Urodelus', 'Kochab', 'Anwar al Farkadain']
    },
    'Anwar al Farkadain': {
      ra: 244.376,
      dec: 75.755,
      connectsTo: ['Ahfa al Farkadain', 'Pherkad']
    }
  },

  Bootes: {
    // Converted from astrotours constellationData.js.
    Arcturus: {
      ra: 213.915,
      dec: 19.183,
      connectsTo: ['Muphrid', 'Rho Bootis', 'Pi Bootis', 'Izar']
    },
    Nekkar: { ra: 225.487, dec: 40.391, connectsTo: ['Seginus', 'Princeps'] },
    Seginus: { ra: 218.02, dec: 38.308, connectsTo: ['Rho Bootis', 'Nekkar'] },
    Princeps: { ra: 228.876, dec: 33.315, connectsTo: ['Nekkar', 'Izar'] },
    Izar: { ra: 221.247, dec: 27.074, connectsTo: ['Princeps', 'Arcturus'] },
    Muphrid: { ra: 208.671, dec: 18.398, connectsTo: ['Arcturus'] },
    'Rho Bootis': {
      ra: 217.957,
      dec: 30.371,
      connectsTo: ['Arcturus', 'Seginus']
    },
    'Zeta Bootis': { ra: 220.287, dec: 13.728, connectsTo: ['Pi Bootis'] },
    'Pi Bootis': {
      ra: 220.182,
      dec: 16.418,
      connectsTo: ['Zeta Bootis', 'Arcturus']
    }
  },

  Draco: {
    // Converted from astrotours constellationData.js.
    Thuban: {
      ra: 211.097,
      dec: 64.376,
      connectsTo: ['Kappa Draconis', 'Edasich']
    },
    Rastaban: {
      ra: 262.608,
      dec: 52.301,
      connectsTo: ['Nu Draconis', 'Eltanin']
    },
    Eltanin: { ra: 269.152, dec: 51.489, connectsTo: ['Rastaban', 'Grumium'] },
    Altais: {
      ra: 288.139,
      dec: 67.662,
      connectsTo: ['Tyl', 'Chi Draconis', 'Aldhibah']
    },
    Tyl: { ra: 297.043, dec: 70.268, connectsTo: ['Altais', 'Chi Draconis'] },
    Aldhibah: {
      ra: 257.197,
      dec: 65.715,
      connectsTo: ['Athebyne', 'Nu Draconis', 'Altais']
    },
    Athebyne: {
      ra: 245.998,
      dec: 61.514,
      connectsTo: ['Theta Draconis', 'Aldhibah']
    },
    'Theta Draconis': {
      ra: 240.472,
      dec: 58.565,
      connectsTo: ['Edasich', 'Athebyne']
    },
    Edasich: {
      ra: 231.232,
      dec: 58.966,
      connectsTo: ['Thuban', 'Theta Draconis']
    },
    'Kappa Draconis': {
      ra: 188.371,
      dec: 69.788,
      connectsTo: ['Giausar', 'Thuban']
    },
    Giausar: { ra: 172.851, dec: 69.331, connectsTo: ['Kappa Draconis'] },
    'Nu Draconis': {
      ra: 263.067,
      dec: 55.173,
      connectsTo: ['Aldhibah', 'Rastaban', 'Grumium']
    },
    Grumium: {
      ra: 268.382,
      dec: 56.873,
      connectsTo: ['Eltanin', 'Nu Draconis']
    },
    'Chi Draconis': { ra: 275.264, dec: 72.733, connectsTo: ['Tyl', 'Altais'] }
  },

  Orion: {
    // Rectangle + belt + head-triangle figure. Coords: Wikipedia infoboxes (J2000).
    Betelgeuse: {
      ra: 88.793,
      dec: 7.407,
      connectsTo: ['Bellatrix', 'Meissa', 'Alnitak']
    },
    Bellatrix: {
      ra: 81.283,
      dec: 6.35,
      connectsTo: ['Betelgeuse', 'Meissa', 'Mintaka']
    },
    Meissa: { ra: 83.784, dec: 9.934, connectsTo: ['Betelgeuse', 'Bellatrix'] },
    Alnitak: {
      ra: 85.19,
      dec: -1.943,
      connectsTo: ['Betelgeuse', 'Alnilam', 'Saiph']
    },
    Alnilam: { ra: 84.053, dec: -1.202, connectsTo: ['Alnitak', 'Mintaka'] },
    Mintaka: {
      ra: 83.002,
      dec: -0.299,
      connectsTo: ['Bellatrix', 'Alnilam', 'Rigel']
    },
    Saiph: { ra: 86.939, dec: -9.67, connectsTo: ['Alnitak', 'Rigel'] },
    Rigel: { ra: 78.634, dec: -8.202, connectsTo: ['Mintaka', 'Saiph'] }
  },

  Cassiopeia: {
    // The "W". Coords: Wikipedia infoboxes (J2000). Gamma Cas has no proper name.
    Caph: { ra: 2.295, dec: 59.15, connectsTo: ['Schedar'] },
    Schedar: { ra: 10.127, dec: 56.537, connectsTo: ['Caph', 'Gamma Cas'] },
    'Gamma Cas': {
      ra: 14.177,
      dec: 60.717,
      connectsTo: ['Schedar', 'Ruchbah']
    },
    Ruchbah: { ra: 21.454, dec: 60.235, connectsTo: ['Gamma Cas', 'Segin'] },
    Segin: { ra: 28.599, dec: 63.67, connectsTo: ['Ruchbah'] }
  },

  Scorpius: {
    // The fishhook. Coords: Wikipedia infoboxes (J2000).
    Acrab: { ra: 241.359, dec: -19.805, connectsTo: ['Dschubba'] },
    Dschubba: {
      ra: 240.083,
      dec: -22.622,
      connectsTo: ['Acrab', 'Pi Sco', 'Alniyat']
    },
    'Pi Sco': { ra: 239.713, dec: -26.114, connectsTo: ['Dschubba'] },
    Alniyat: { ra: 245.297, dec: -25.593, connectsTo: ['Dschubba', 'Antares'] },
    Antares: {
      ra: 247.352,
      dec: -26.432,
      connectsTo: ['Alniyat', 'Paikauhale']
    },
    Paikauhale: {
      ra: 248.971,
      dec: -28.216,
      connectsTo: ['Antares', 'Larawag']
    },
    Larawag: {
      ra: 252.541,
      dec: -34.293,
      connectsTo: ['Paikauhale', 'Xamidimura']
    },
    Xamidimura: {
      ra: 252.968,
      dec: -38.047,
      connectsTo: ['Larawag', 'Zeta Sco']
    },
    'Zeta Sco': {
      ra: 253.646,
      dec: -42.361,
      connectsTo: ['Xamidimura', 'Eta Sco']
    },
    'Eta Sco': {
      ra: 258.038,
      dec: -43.239,
      connectsTo: ['Zeta Sco', 'Sargas']
    },
    Sargas: { ra: 264.33, dec: -42.998, connectsTo: ['Eta Sco', 'Iota Sco'] },
    'Iota Sco': { ra: 266.896, dec: -40.127, connectsTo: ['Sargas', 'Girtab'] },
    Girtab: { ra: 265.622, dec: -39.03, connectsTo: ['Iota Sco', 'Shaula'] },
    Shaula: { ra: 263.402, dec: -37.104, connectsTo: ['Girtab', 'Lesath'] },
    Lesath: { ra: 262.691, dec: -37.296, connectsTo: ['Shaula'] }
  },

  Cygnus: {
    // The Northern Cross. Coords: Wikipedia infoboxes (J2000).
    Deneb: { ra: 310.358, dec: 45.28, connectsTo: ['Sadr'] },
    Sadr: {
      ra: 305.557,
      dec: 40.257,
      connectsTo: ['Deneb', 'Gienah', 'Delta Cyg', 'Albireo']
    },
    Gienah: { ra: 311.553, dec: 33.97, connectsTo: ['Sadr'] },
    'Delta Cyg': { ra: 296.244, dec: 45.131, connectsTo: ['Sadr'] },
    Albireo: { ra: 292.679, dec: 27.96, connectsTo: ['Sadr'] }
  },

  Lyra: {
    // Vega + the parallelogram. Coords: Wikipedia infoboxes (J2000).
    Vega: { ra: 279.235, dec: 38.784, connectsTo: ['Zeta Lyr'] },
    'Zeta Lyr': {
      ra: 281.193,
      dec: 37.605,
      connectsTo: ['Vega', 'Delta Lyr', 'Sheliak']
    },
    'Delta Lyr': {
      ra: 283.626,
      dec: 36.899,
      connectsTo: ['Zeta Lyr', 'Sulafat']
    },
    Sulafat: { ra: 284.736, dec: 32.69, connectsTo: ['Delta Lyr', 'Sheliak'] },
    Sheliak: { ra: 282.52, dec: 33.363, connectsTo: ['Sulafat', 'Zeta Lyr'] }
  },

  Aquila: {
    // The eagle. Coords: Wikipedia infoboxes (J2000).
    Altair: {
      ra: 297.696,
      dec: 8.868,
      connectsTo: ['Alshain', 'Tarazed', 'Delta Aql']
    },
    Tarazed: { ra: 296.565, dec: 10.613, connectsTo: ['Altair'] },
    Alshain: { ra: 298.828, dec: 6.407, connectsTo: ['Altair'] },
    'Delta Aql': {
      ra: 291.375,
      dec: 3.115,
      connectsTo: ['Altair', 'Eta Aql', 'Zeta Aql', 'Lambda Aql']
    },
    'Eta Aql': {
      ra: 298.118,
      dec: 1.006,
      connectsTo: ['Delta Aql', 'Theta Aql']
    },
    'Theta Aql': { ra: 302.826, dec: -0.821, connectsTo: ['Eta Aql'] },
    'Zeta Aql': {
      ra: 286.353,
      dec: 13.863,
      connectsTo: ['Delta Aql', 'Epsilon Aql']
    },
    'Lambda Aql': { ra: 286.562, dec: -4.883, connectsTo: ['Delta Aql'] },
    'Epsilon Aql': { ra: 284.906, dec: 15.068, connectsTo: ['Zeta Aql'] }
  },

  Sagittarius: {
    // The Teapot. Coords: Wikipedia infoboxes (J2000).
    'Kaus Australis': {
      ra: 276.043,
      dec: -34.385,
      connectsTo: ['Kaus Media', 'Ascella']
    },
    'Kaus Media': {
      ra: 275.249,
      dec: -29.828,
      connectsTo: ['Alnasl', 'Kaus Australis', 'Kaus Borealis']
    },
    'Kaus Borealis': {
      ra: 276.993,
      dec: -25.422,
      connectsTo: ['Kaus Media', 'Phi Sgr']
    },
    'Phi Sgr': {
      ra: 281.414,
      dec: -26.991,
      connectsTo: ['Kaus Borealis', 'Ascella', 'Nunki']
    },
    Nunki: { ra: 283.816, dec: -26.297, connectsTo: ['Phi Sgr', 'Tau Sgr'] },
    'Tau Sgr': { ra: 286.735, dec: -27.67, connectsTo: ['Nunki', 'Ascella'] },
    Ascella: {
      ra: 285.653,
      dec: -29.88,
      connectsTo: ['Kaus Australis', 'Phi Sgr', 'Tau Sgr']
    },
    Alnasl: { ra: 271.452, dec: -30.424, connectsTo: ['Kaus Media'] }
  },

  Pegasus: {
    // Great Square + neck + forelegs. Coords: Wikipedia infoboxes (J2000).
    Markab: {
      ra: 346.19,
      dec: 15.205,
      connectsTo: ['Algenib', 'Scheat', 'Xi Peg']
    },
    Scheat: {
      ra: 345.944,
      dec: 28.083,
      connectsTo: ['Alpheratz', 'Markab', 'Matar', 'Sadalbari']
    },
    Algenib: { ra: 3.309, dec: 15.184, connectsTo: ['Alpheratz', 'Markab'] },
    Alpheratz: { ra: 2.097, dec: 29.09, connectsTo: ['Algenib', 'Scheat'] },
    Enif: { ra: 326.046, dec: 9.875, connectsTo: ['Biham'] },
    Homam: { ra: 340.366, dec: 10.831, connectsTo: ['Biham', 'Xi Peg'] },
    Biham: { ra: 332.55, dec: 6.198, connectsTo: ['Enif', 'Homam'] },
    Matar: { ra: 340.751, dec: 30.221, connectsTo: ['Pi Peg', 'Scheat'] },
    Sadalbari: {
      ra: 342.501,
      dec: 24.602,
      connectsTo: ['Lambda Peg', 'Scheat']
    },
    'Xi Peg': { ra: 341.673, dec: 12.173, connectsTo: ['Homam', 'Markab'] },
    'Pi Peg': { ra: 332.307, dec: 33.172, connectsTo: ['Matar'] },
    'Lambda Peg': {
      ra: 341.633,
      dec: 23.566,
      connectsTo: ['Iota Peg', 'Sadalbari']
    },
    'Iota Peg': {
      ra: 331.753,
      dec: 25.345,
      connectsTo: ['Kappa Peg', 'Lambda Peg']
    },
    'Kappa Peg': { ra: 326.161, dec: 25.645, connectsTo: ['Iota Peg'] }
  },

  Crux: {
    // The Southern Cross — two crossing segments. Coords: Wikipedia infoboxes (J2000).
    Gacrux: { ra: 187.792, dec: -57.113, connectsTo: ['Acrux'] },
    Acrux: { ra: 186.65, dec: -63.099, connectsTo: ['Gacrux'] },
    'Delta Cru': { ra: 183.786, dec: -58.749, connectsTo: ['Mimosa'] },
    Mimosa: { ra: 191.93, dec: -59.689, connectsTo: ['Delta Cru'] }
  },

  Taurus: {
    // Face (Hyades V) + horns. Coords: SIMBAD ICRS/J2000; figure: Stellarium modern_iau.
    Aldebaran: {
      ra: 68.98,
      dec: 16.509,
      connectsTo: ['Zeta Tau', 'Theta Tau']
    },
    'Zeta Tau': { ra: 84.411, dec: 21.143, connectsTo: ['Aldebaran'] },
    'Theta Tau': {
      ra: 67.166,
      dec: 15.871,
      connectsTo: ['Aldebaran', 'Gamma Tau']
    },
    'Gamma Tau': {
      ra: 64.948,
      dec: 15.628,
      connectsTo: ['Theta Tau', 'Delta Tau']
    },
    'Delta Tau': { ra: 65.734, dec: 17.543, connectsTo: ['Gamma Tau', 'Ain'] },
    Ain: { ra: 67.154, dec: 19.18, connectsTo: ['Delta Tau', 'Elnath'] },
    Elnath: { ra: 81.573, dec: 28.607, connectsTo: ['Ain'] }
  },

  Gemini: {
    // The twins. Coords: SIMBAD ICRS/J2000; figure: Stellarium modern_iau.
    Castor: { ra: 113.649, dec: 31.888, connectsTo: ['Tau Gem'] },
    Pollux: { ra: 116.329, dec: 28.026, connectsTo: ['Upsilon Gem'] },
    'Tau Gem': {
      ra: 107.785,
      dec: 30.245,
      connectsTo: ['Castor', 'Iota Gem', 'Theta Gem', 'Mebsuta']
    },
    'Theta Gem': { ra: 103.197, dec: 33.961, connectsTo: ['Tau Gem'] },
    'Iota Gem': {
      ra: 111.432,
      dec: 27.798,
      connectsTo: ['Tau Gem', 'Upsilon Gem']
    },
    'Upsilon Gem': {
      ra: 113.981,
      dec: 26.896,
      connectsTo: ['Pollux', 'Iota Gem', 'Kappa Gem', 'Wasat']
    },
    'Kappa Gem': { ra: 116.112, dec: 24.398, connectsTo: ['Upsilon Gem'] },
    Wasat: {
      ra: 110.031,
      dec: 21.982,
      connectsTo: ['Upsilon Gem', 'Mekbuda', 'Lambda Gem']
    },
    Mekbuda: { ra: 106.027, dec: 20.57, connectsTo: ['Wasat', 'Alhena'] },
    Alhena: { ra: 99.428, dec: 16.399, connectsTo: ['Mekbuda'] },
    'Lambda Gem': { ra: 109.523, dec: 16.54, connectsTo: ['Wasat', 'Alzirr'] },
    Alzirr: { ra: 101.322, dec: 12.896, connectsTo: ['Lambda Gem'] },
    Mebsuta: {
      ra: 100.983,
      dec: 25.131,
      connectsTo: ['Tau Gem', 'Nu Gem', 'Tejat']
    },
    'Nu Gem': { ra: 97.241, dec: 20.212, connectsTo: ['Mebsuta'] },
    Tejat: { ra: 95.74, dec: 22.514, connectsTo: ['Mebsuta', 'Propus'] },
    Propus: { ra: 93.719, dec: 22.507, connectsTo: ['Tejat', '1 Gem'] },
    '1 Gem': { ra: 91.03, dec: 23.263, connectsTo: ['Propus'] }
  },

  'Canis Major': {
    // The great dog. Coords: SIMBAD ICRS/J2000; figure: Stellarium modern_iau.
    Sirius: {
      ra: 101.287,
      dec: -16.716,
      connectsTo: ['Mirzam', 'Wezen', 'Iota CMa']
    },
    Mirzam: { ra: 95.675, dec: -17.956, connectsTo: ['Sirius', 'Nu2 CMa'] },
    'Nu2 CMa': {
      ra: 99.171,
      dec: -19.256,
      connectsTo: ['Mirzam', 'Omicron1 CMa']
    },
    'Omicron1 CMa': {
      ra: 103.533,
      dec: -24.184,
      connectsTo: ['Nu2 CMa', 'Adhara']
    },
    Adhara: {
      ra: 104.656,
      dec: -28.972,
      connectsTo: ['Omicron1 CMa', 'Wezen']
    },
    Wezen: {
      ra: 107.098,
      dec: -26.393,
      connectsTo: ['Adhara', 'Sirius', 'Aludra']
    },
    Aludra: { ra: 111.024, dec: -29.303, connectsTo: ['Wezen'] },
    'Iota CMa': {
      ra: 104.034,
      dec: -17.054,
      connectsTo: ['Sirius', 'Muliphein', 'Theta CMa']
    },
    Muliphein: {
      ra: 105.94,
      dec: -15.633,
      connectsTo: ['Iota CMa', 'Theta CMa']
    },
    'Theta CMa': {
      ra: 103.547,
      dec: -12.039,
      connectsTo: ['Muliphein', 'Iota CMa']
    }
  }
};

/** Constellation names in menu display order (alphabetical). */
export const CONSTELLATION_NAMES = Object.keys(CATALOG).sort((a, b) =>
  a.localeCompare(b)
);
